// src/modules/communications/context/SoftphoneContext.js
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { Device, Call } from '@twilio/voice-sdk';
import { getSoftphoneToken, searchLeads } from '../services/softphoneService';

const SoftphoneContext = createContext();

export const useSoftphone = () => {
  const context = useContext(SoftphoneContext);
  if (!context) {
    throw new Error('useSoftphone must be used within a SoftphoneProvider');
  }
  return context;
};

export const SoftphoneProvider = ({ children }) => {
  const deviceRef = useRef(null);
  const activeCallRef = useRef(null);
  const durationIntervalRef = useRef(null);
  const ringtoneRef = useRef(null);

  const [deviceState, setDeviceState] = useState('unregistered'); // unregistered, registering, registered, error
  const [callState, setCallState] = useState('idle'); // idle, incoming, connecting, connected
  const [callDirection, setCallDirection] = useState(null);
  const [callerInfo, setCallerInfo] = useState(null); // { number, name, leadId }
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [error, setError] = useState(null);
  const [isWidgetExpanded, setIsWidgetExpanded] = useState(false);

  // Audio devices
  const [audioInputDevices, setAudioInputDevices] = useState([]);
  const [audioOutputDevices, setAudioOutputDevices] = useState([]);

  const isAuthenticated = !!localStorage.getItem('token');

  // Cleanup duration timer
  const stopDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, []);

  const startDurationTimer = useCallback(() => {
    stopDurationTimer();
    setCallDuration(0);
    durationIntervalRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
  }, [stopDurationTimer]);

  // Ringtone
  const playRingtone = useCallback(() => {
    try {
      if (!ringtoneRef.current) {
        // Use a simple oscillating tone via Web Audio API
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        oscillator.frequency.value = 440;
        oscillator.type = 'sine';
        gainNode.gain.value = 0.3;

        // Create a ringing pattern
        const ringInterval = setInterval(() => {
          gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
          gainNode.gain.setValueAtTime(0, audioCtx.currentTime + 0.8);
        }, 2000);

        oscillator.start();
        ringtoneRef.current = { audioCtx, oscillator, gainNode, ringInterval };
      }
    } catch (e) {
      console.warn('Could not play ringtone:', e);
    }
  }, []);

  const stopRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      try {
        clearInterval(ringtoneRef.current.ringInterval);
        ringtoneRef.current.oscillator.stop();
        ringtoneRef.current.audioCtx.close();
      } catch (e) { /* ignore */ }
      ringtoneRef.current = null;
    }
  }, []);

  // Reset call state
  const resetCallState = useCallback(() => {
    activeCallRef.current = null;
    setCallState('idle');
    setCallDirection(null);
    setCallerInfo(null);
    setIsMuted(false);
    setCallDuration(0);
    stopDurationTimer();
    stopRingtone();
  }, [stopDurationTimer, stopRingtone]);

  // Look up lead by phone number
  const lookupLead = useCallback(async (phoneNumber) => {
    try {
      // Clean the phone number for search (strip +1, client: prefix)
      let cleanNumber = phoneNumber;
      if (cleanNumber.startsWith('client:')) return { number: phoneNumber, name: null, leadId: null };
      const digits = cleanNumber.replace(/\D/g, '');
      // Use last 10 digits for search (strip country code)
      const searchDigits = digits.length > 10 ? digits.slice(-10) : digits;
      if (!searchDigits) return { number: phoneNumber, name: null, leadId: null };

      const results = await searchLeads(searchDigits);
      const leads = Array.isArray(results) ? results : (results.leads || results.rows || []);
      if (leads.length > 0) {
        // Find best match — exact phone match preferred
        const exactMatch = leads.find(l => {
          const leadDigits = (l.phone || '').replace(/\D/g, '').slice(-10);
          return leadDigits === searchDigits;
        });
        const lead = exactMatch || leads[0];
        return { number: phoneNumber, name: lead.name, leadId: lead.id };
      }
    } catch (e) {
      // Silently fail — caller info is optional
    }
    return { number: phoneNumber, name: null, leadId: null };
  }, []);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Show browser desktop notification
  const showDesktopNotification = useCallback((title, body) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body,
        icon: '/favicon.ico',
        tag: 'incoming-call',
        requireInteraction: true,
      });
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
      return notification;
    }
  }, []);

  // Handle incoming call
  const handleIncomingCall = useCallback(async (call) => {
    activeCallRef.current = call;
    setCallState('incoming');
    setCallDirection('inbound');

    const fromNumber = call.parameters?.From || 'Unknown';
    setCallerInfo({ number: fromNumber, name: null, leadId: null });

    // Show desktop notification immediately with phone number
    const digits = fromNumber.replace(/\D/g, '');
    const displayNumber = digits.length === 11 && digits.startsWith('1')
      ? `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
      : fromNumber;

    // The ring cascade attaches the caller's identity to the call as custom
    // parameters, so we can render immediately with no network round-trip. They
    // are absent when the cascade is off, or on call paths that don't inject
    // them — fall back to the lead lookup exactly as before. Every value is a
    // string, and '' means absent.
    const params = call.customParameters;
    const displayLabel = params?.get('display_label') || '';
    const leadName = params?.get('lead_name') || '';
    const leadIdParam = params?.get('lead_id') || '';

    let desktopNotif;

    if (displayLabel) {
      setCallerInfo({
        number: fromNumber,
        name: leadName || null,
        leadId: leadIdParam ? Number(leadIdParam) : null,
      });
      desktopNotif = showDesktopNotification('Incoming Call', `${displayLabel} — ${displayNumber}`);
    } else {
      desktopNotif = showDesktopNotification('Incoming Call', displayNumber);

      // Async lead lookup — update notification with lead name when found
      lookupLead(fromNumber).then(info => {
        setCallerInfo(info);
        if (info.name && desktopNotif) {
          desktopNotif.close();
          showDesktopNotification('Incoming Call', `${info.name} — ${displayNumber}`);
        }
      });
    }

    playRingtone();

    call.on('cancel', () => {
      if (desktopNotif) desktopNotif.close();
      resetCallState();
    });

    call.on('disconnect', () => {
      if (desktopNotif) desktopNotif.close();
      resetCallState();
    });

    call.on('error', (err) => {
      console.error('Call error:', err);
      if (desktopNotif) desktopNotif.close();
      setError(err.message || 'Call error');
      resetCallState();
    });
  }, [lookupLead, playRingtone, resetCallState, showDesktopNotification]);

  // Initialize the Twilio Device
  const initializeDevice = useCallback(async () => {
    if (deviceRef.current) return;
    if (!isAuthenticated) return;

    try {
      setDeviceState('registering');
      setError(null);

      const { token, identity } = await getSoftphoneToken();

      const device = new Device(token, {
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
        closeProtection: 'A call is currently in progress. Closing this tab will end the call.',
        edge: 'ashburn',
      });

      device.on('registered', () => {
        setDeviceState('registered');
        setError(null);
        console.log('Softphone device registered as:', identity);
        // Enumerate audio devices
        updateAudioDevices(device);
      });

      device.on('unregistered', () => {
        setDeviceState('unregistered');
      });

      device.on('error', (err) => {
        console.error('Softphone device error:', err);
        setDeviceState('error');
        setError(err.message || 'Device error');
      });

      device.on('incoming', handleIncomingCall);

      device.on('tokenWillExpire', async () => {
        try {
          const { token: newToken } = await getSoftphoneToken();
          device.updateToken(newToken);
        } catch (e) {
          console.error('Error refreshing softphone token:', e);
        }
      });

      await device.register();
      deviceRef.current = device;
    } catch (err) {
      console.error('Error initializing softphone device:', err);
      setDeviceState('error');
      setError(err.message || 'Failed to initialize softphone');
    }
  }, [isAuthenticated, handleIncomingCall]);

  // Get audio devices
  const updateAudioDevices = (device) => {
    try {
      if (device?.audio) {
        const inputs = Array.from(device.audio.availableInputDevices.values());
        const outputs = Array.from(device.audio.availableOutputDevices.values());
        setAudioInputDevices(inputs);
        setAudioOutputDevices(outputs);
      }
    } catch (e) {
      console.warn('Could not enumerate audio devices:', e);
    }
  };

  // Answer incoming call
  const answerCall = useCallback(() => {
    if (activeCallRef.current && callState === 'incoming') {
      stopRingtone();
      activeCallRef.current.accept();
      setCallState('connected');
      startDurationTimer();
    }
  }, [callState, stopRingtone, startDurationTimer]);

  // Reject incoming call
  const rejectCall = useCallback(() => {
    if (activeCallRef.current && callState === 'incoming') {
      activeCallRef.current.reject();
      resetCallState();
    }
  }, [callState, resetCallState]);

  // Make an outbound call
  const makeCall = useCallback(async (phoneNumber, leadId = null) => {
    if (!deviceRef.current || deviceState !== 'registered') {
      setError('Softphone not connected');
      return;
    }

    if (callState !== 'idle') {
      setError('A call is already in progress');
      return;
    }

    try {
      setCallState('connecting');
      setCallDirection('outbound');
      setCallerInfo({ number: phoneNumber, name: null, leadId });
      setError(null);

      // Look up lead info
      if (leadId) {
        lookupLead(phoneNumber).then(info => {
          setCallerInfo(prev => ({ ...prev, name: info.name, leadId: leadId || info.leadId }));
        });
      }

      const call = await deviceRef.current.connect({
        params: {
          To: phoneNumber,
          LeadId: leadId || '',
        },
        rtcConstraints: {
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          }
        }
      });

      activeCallRef.current = call;

      call.on('accept', () => {
        setCallState('connected');
        startDurationTimer();
      });

      call.on('disconnect', () => {
        resetCallState();
      });

      call.on('cancel', () => {
        resetCallState();
      });

      call.on('error', (err) => {
        console.error('Outbound call error:', err);
        setError(err.message || 'Call error');
        resetCallState();
      });
    } catch (err) {
      console.error('Error making call:', err);
      setError(err.message || 'Failed to make call');
      resetCallState();
    }
  }, [deviceState, callState, lookupLead, startDurationTimer, resetCallState]);

  // Hang up
  const hangupCall = useCallback(() => {
    if (activeCallRef.current) {
      activeCallRef.current.disconnect();
      resetCallState();
    }
  }, [resetCallState]);

  // Mute/unmute
  const toggleMute = useCallback(() => {
    if (activeCallRef.current) {
      const newMuted = !isMuted;
      activeCallRef.current.mute(newMuted);
      setIsMuted(newMuted);
    }
  }, [isMuted]);

  // Send DTMF
  const sendDTMF = useCallback((digit) => {
    if (activeCallRef.current) {
      activeCallRef.current.sendDigits(digit);
    }
  }, []);

  // Disconnect / destroy device
  const destroyDevice = useCallback(() => {
    if (deviceRef.current) {
      deviceRef.current.destroy();
      deviceRef.current = null;
      setDeviceState('unregistered');
    }
    resetCallState();
  }, [resetCallState]);

  // Select audio input device
  const selectAudioInput = useCallback(async (deviceId) => {
    if (deviceRef.current?.audio) {
      try {
        await deviceRef.current.audio.setInputDevice(deviceId);
      } catch (e) {
        console.warn('Could not set input device:', e);
      }
    }
  }, []);

  // Select audio output device
  const selectAudioOutput = useCallback(async (deviceId) => {
    if (deviceRef.current?.audio) {
      try {
        await deviceRef.current.audio.speakerDevices.set(deviceId);
      } catch (e) {
        console.warn('Could not set output device:', e);
      }
    }
  }, []);

  // Auto-initialize when authenticated
  useEffect(() => {
    if (isAuthenticated && deviceState === 'unregistered') {
      initializeDevice();
    }
    return () => {
      // Cleanup on unmount
      if (deviceRef.current) {
        deviceRef.current.destroy();
        deviceRef.current = null;
      }
      stopDurationTimer();
      stopRingtone();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const value = {
    // State
    deviceState,
    callState,
    callDirection,
    callerInfo,
    isMuted,
    callDuration,
    error,
    isWidgetExpanded,
    audioInputDevices,
    audioOutputDevices,

    // Actions
    initializeDevice,
    destroyDevice,
    answerCall,
    rejectCall,
    makeCall,
    hangupCall,
    toggleMute,
    sendDTMF,
    setIsWidgetExpanded,
    selectAudioInput,
    selectAudioOutput,
    setError,
  };

  return (
    <SoftphoneContext.Provider value={value}>
      {children}
    </SoftphoneContext.Provider>
  );
};
